import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Department } from './DepartmentManager';
import { AppPagination } from '../../ui/AppPagination';
import { JoySelectField } from '../../ui/JoySelectField';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// Получаем базовый URL API из переменной окружения .env
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

type ApiProgramRead = {
  id: number;
  name: string;
  department: { id: number; name: string } | null;
};

type ApiProgramListResponse = {
  total: number;
  page: number;
  size: number;
  items: ApiProgramRead[];
};

export interface ProgramWithDept {
    id: string;
    name: string;
    departmentId: string | null;
}

interface ProgramRowProps {
    program: ProgramWithDept;
    departments: Department[];
    onDepartmentChange: (programId: string, departmentId: string | null) => Promise<void>;
}

const DepartmentSelector: React.FC<ProgramRowProps> = ({ program, departments, onDepartmentChange }) => {
    const [selectedValue, setSelectedValue] = useState<string | null>(program.departmentId);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        setSelectedValue(program.departmentId);
    }, [program.departmentId]);

    const options = useMemo(() => [
        { value: 'null', label: 'Не назначен' },
        ...departments.map(d => ({ value: d.id, label: d.name }))
    ], [departments]);

    const handleChange = async (newValue: string | null) => {
        setIsUpdating(true);
        const finalValue = newValue === 'null' ? null : newValue;
        try {
            await onDepartmentChange(program.id, finalValue);
            setSelectedValue(finalValue);
        } catch (error: any) {
            alert(`Не удалось обновить отдел: ${error.message}`);
            setSelectedValue(program.departmentId);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="flex items-center gap-2">
            <div className="flex-1">
                <JoySelectField
                    label=""
                    value={selectedValue}
                    onChange={handleChange}
                    options={options}
                    placeholder="Выберите отдел..."
                    disabled={isUpdating}
                />
            </div>
            {isUpdating && <CircularProgress size={20} />}
        </div>
    );
};

interface ProgramsListProps {
  departments: Department[];
  refreshTrigger: number;
  onRefresh: () => void;
  onLogout?: () => void;
}

export const ProgramsList: React.FC<ProgramsListProps> = ({ departments, refreshTrigger, onRefresh, onLogout }) => {
    const [programs, setPrograms] = useState<ProgramWithDept[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<{ message: string, error: boolean } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pageSize = 15;

    const getAuthHeaders = (includeContentType = true): HeadersInit => {
        const token = localStorage.getItem('token');
        const headers: HeadersInit = {
            "Accept": "application/json",
            ...(token ? { "Authorization": token } : {})
        };
        if (includeContentType) {
            headers["Content-Type"] = "application/json";
        }
        return headers;
    };

    const handleUnauthorized = (status: number) => {
        if (status === 401 && onLogout) {
            onLogout();
            return true;
        }
        return false;
    };

    useEffect(() => {
        const fetchPrograms = async () => {
            setLoading(true);
            try {
                // Заменили хардкод адреса на API_BASE_URL
                const url = `${API_BASE_URL}/api/v1/programs/?page=${page}&size=${pageSize}`;
                const res = await fetch(url, { headers: getAuthHeaders() });

                if (handleUnauthorized(res.status)) return;
                if (!res.ok) throw new Error("Failed to fetch programs");

                const data: ApiProgramListResponse = await res.json();

                setPrograms(data.items.map(p => ({
                    id: String(p.id),
                    name: p.name,
                    departmentId: p.department ? String(p.department.id) : null,
                })));
                setTotalItems(data.total);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchPrograms();
    }, [page, refreshTrigger]);

    const handleDepartmentChange = async (programId: string, departmentId: string | null) => {
        // Заменили хардкод адреса на API_BASE_URL
        const url = `${API_BASE_URL}/api/v1/programs/${programId}`;
        const payload = { department_id: departmentId ? Number(departmentId) : null };

        const res = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });

        if (handleUnauthorized(res.status)) return;

        if (!res.ok) {
            const err = await res.json().catch(() => ({ detail: "Failed to update program" }));
            throw new Error(err.detail);
        }
    };

    const handleDownloadTemplate = async () => {
        setDownloading(true);
        try {
            const token = localStorage.getItem('token');
            // Заменили хардкод адреса на API_BASE_URL
            const url = `${API_BASE_URL}/api/v1/program-departments/template`;

            const res = await fetch(url, {
                method: 'GET',
                headers: {
                    ...(token ? { "Authorization": token } : {})
                }
            });

            if (handleUnauthorized(res.status)) return;
            if (!res.ok) throw new Error('Ошибка при скачивании');

            const blob = await res.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error: any) {
            setUploadStatus({ message: `Ошибка скачивания: ${error.message}`, error: true });
        } finally {
            setDownloading(false);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const token = localStorage.getItem('token');
        setUploading(true);
        setUploadStatus(null);
        const formData = new FormData();
        formData.append('file', file);

        try {
            // Заменили хардкод адреса на API_BASE_URL
            const res = await fetch(`${API_BASE_URL}/api/v1/program-departments/upload`, {
                method: 'POST',
                headers: {
                    ...(token ? { "Authorization": token } : {})
                },
                body: formData,
            });

            if (handleUnauthorized(res.status)) return;

            const result = await res.json();
            if (!res.ok) throw new Error(result.detail || 'Upload failed');
            setUploadStatus({ message: `Успешно! Обновлено: ${result.updated_programs}, создано: ${result.new_departments_created}.`, error: false });
            onRefresh();
        } catch (error: any) {
            setUploadStatus({ message: error.message, error: true });
        } finally {
            setUploading(false);
            if(fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <header className="flex items-center justify-between gap-4 p-4 sm:p-5 border-b border-gray-200">
                <h1 className="text-lg font-medium text-gray-900">Программы обучения</h1>
                <div className="flex items-center gap-3">
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".xlsx, .xls" />
                    <button
                        onClick={handleDownloadTemplate}
                        disabled={downloading}
                        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors disabled:text-gray-400"
                    >
                        {downloading ? <CircularProgress size={18} /> : <FileDownloadIcon fontSize="small"/>}
                        {downloading ? 'Скачивание...' : 'Скачать шаблон'}
                    </button>
                    <button onClick={handleUploadClick} disabled={uploading} className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#1E61AF] px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                        {uploading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon fontSize="small" />}
                        {uploading ? 'Загрузка...' : 'Загрузить файл'}
                    </button>
                </div>
            </header>

            {/* Код разметки таблицы остался без изменений */}
            {uploadStatus && (
                <div className={`px-6 py-3 text-sm border-b ${uploadStatus.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    {uploadStatus.message}
                </div>
            )}

            <div className="relative">
                {loading && (
                    <div className="absolute inset-0 bg-white/70 z-10 flex justify-center items-center backdrop-blur-sm">
                        <CircularProgress />
                    </div>
                )}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="pl-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">№</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Название программы</th>
                                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3 pr-12 lg:pr-20">Отдел</th>
                            </tr>
                        </thead>
                        <tbody className={`bg-white divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}>
                            {programs.map((p, index) => (
                                <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="pl-6 py-4 whitespace-nowrap text-sm text-gray-500">{(page - 1) * pageSize + index + 1}</td>
                                    <td className="px-4 py-4 text-sm font-medium text-gray-900 leading-tight">{p.name}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 pr-12 lg:pr-20">
                                        <DepartmentSelector
                                            program={p}
                                            departments={departments}
                                            onDepartmentChange={handleDepartmentChange}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                     {programs.length === 0 && !loading && (
                        <div className="text-center py-10 text-gray-500">
                            Программы не найдены.
                        </div>
                    )}
                </div>
            </div>
             <div className="px-6 py-4 border-t bg-gray-50">
                <AppPagination
                    page={page}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onChange={(newPage: number) => setPage(newPage)}
                />
            </div>
        </div>
    );
};